// ============================================================================
// messages.ts — ВСТРОЕННЫЕ словари клиента (этап 2 мультиязычности).
//
// Зачем встроенные словари, если есть таблица translations:
//   1) оболочка/вход/лаунчер работают ДО входа и без активной базы — серверные
//      словари в этот момент недоступны;
//   2) язык по умолчанию должен быть понятен даже при пустой таблице;
//   3) серверные переводы (metadata.GetTranslations) НАКЛАДЫВАЮТСЯ поверх
//      встроенных — правка текста в конфигураторе не требует пересборки.
//
// Правило: ключ — латиница с точками (nav.*, chat.*, market.*, psy.*), значение —
// только текст. Плейсхолдеры вида {count} подставляет translate().
// ============================================================================

export type Messages = Record<string, string>;

/** Язык по умолчанию платформы (совпадает с системным языком ядра). */
export const BASE_LOCALE = 'ru';

/** Языки, для которых в клиент встроен словарь. Серверные языки добавляются
 *  к этому списку (переключатель в профиле). */
export const BUILTIN_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
];

const ru: Messages = {
  // --- Оболочка / навигация -------------------------------------------------
  'nav.start': 'Пуск',
  'nav.start_hint': 'Пуск (профиль, поиск, разделы, приложения, выход)',
  'nav.worktable': 'Рабочий стол',
  'nav.invite': 'Пригласить',
  'nav.sections': 'Разделы',
  'nav.chat': 'Чат',
  'nav.assistant': 'Мой ассистент',
  'nav.files': 'Файлы',
  'nav.mail': 'Почта',
  'nav.support': 'Поддержка',
  'nav.cabinet': 'Кабинет',
  'nav.patient_cabinet': 'Мой кабинет',
  'nav.medic_appointments': 'Запись на приём',
  'nav.voice_rooms': 'Голосовые комнаты',
  'nav.back': 'Назад',
  'nav.enterprise': 'Предприятие',
  'nav.contour': 'контур',
  'nav.contour_hint': 'Закрытый контур: внешние сервисы отключены, уведомления локальные',
  'nav.search_sections': 'Поиск',
  'nav.loading_sections': 'Загрузка разделов…',
  'nav.sections_error': 'Разделы не загрузились. Нажмите для повторной попытки.',
  'nav.nothing_found': 'Ничего не найдено',
  'nav.logout': 'Выйти из аккаунта',
  'nav.profile': 'Профиль',
  'nav.profile_settings': 'Профиль и настройки',
  'nav.user': 'Пользователь',
  'nav.connection_online': 'Подключение активно',
  'nav.connection_reconnecting': 'Переподключение…',
  'nav.connection_offline': 'Нет подключения к сети',
  'nav.offline_mode': 'Автономный режим (работа без сервера)',

  // --- Настройки / меню пользователя ---------------------------------------
  'settings.title': 'Настройки',
  'settings.done': 'Готово',
  'settings.tab.profile': 'Профиль',
  'settings.tab.assistant': 'Мой ассистент',
  'settings.tab.language': 'Язык',
  'settings.tab.devices': 'Входы',
  'settings.tab.links': 'Связанные аккаунты',
  'settings.tab.privacy': 'Приватность',
  'settings.tab.appearance': 'Оформление',
  'settings.tab.themes': 'Темы',
  'settings.tab.notifications': 'Уведомления',
  'settings.tab.app': 'Приложение',
  'settings.menu.assistant': 'Мой ассистент',
  'settings.menu.appearance': 'Оформление',
  'settings.menu.themes': 'Темы оформления',
  'settings.menu.permissions': 'Разрешения',
  'settings.menu.notifications': 'Уведомления',
  'settings.menu.app': 'Приложение',
  'settings.menu.account': 'Мой счёт',
  'settings.menu.invite': 'Пригласить',
  'settings.menu.about': 'О программе',
  'settings.about.platform': 'Платформа бизнес-приложений',
  'settings.about.configuration': 'Конфигурация',
  'settings.about.version': 'Версия платформы',
  'settings.about.close': 'Закрыть',
  'settings.language.title': 'Язык интерфейса',
  'settings.language.hint': 'Выберите язык. Если для него ещё нет перевода, тексты будут на языке базы.',
  'settings.language.default': 'Язык базы: {locale}',
  'settings.language.fallback': 'Пока нет перевода — показываем: {locale}',
  'settings.language.current': 'Текущий язык',
  'settings.language.saved': 'Язык сохранён',
  'settings.language.offline': 'Язык сохранён локально; на сервер уйдёт при восстановлении связи.',

  // --- Чат ------------------------------------------------------------------
  'chat.tab.dialogs': 'Диалоги',
  'chat.tab.discussions': 'Обсуждения',
  'chat.tab.rooms': 'Комнаты',
  'chat.title': 'Чаты',
  'chat.new': 'Новый чат',
  'chat.group': 'Группа',
  'chat.search': 'Поиск чатов…',
  'chat.search_messages': 'Поиск по сообщениям',
  'chat.search_messages_hint': 'Поиск по сообщениям (все чаты и фильтры)',
  'chat.select': 'Выберите чат слева или создайте новый',
  'chat.empty': 'Диалогов нет. Создайте новый чат.',
  'chat.empty_archive': 'В архиве пусто.',
  'chat.empty_discussions': 'Обсуждений нет. Откройте обсуждение у объекта (кнопка 💬 в форме).',
  'chat.pinned': 'Закреплённые',
  'chat.archive': 'Архив ({count})',
  'chat.back_to_dialogs': '← Вернуться к диалогам',
  'chat.no_messages': 'Сообщений нет',
  'chat.loading': 'Загрузка…',
  'chat.message': 'Сообщение…',
  'chat.reply': 'Ответ в обсуждении…',
  'chat.no_replies': 'Ответов пока нет — начните обсуждение',
  'chat.hide_self': 'У себя',
  'chat.hide_self_hint': 'Скрыть сообщения у себя',
  'chat.hide_all': 'У всех',
  'chat.hide_all_hint': 'Пометить сообщения на удаление у всех',
  'chat.permanent': 'Окончательно',
  'chat.permanent_hint': 'Физически удалить все сообщения',
  'chat.new_title': 'Новый чат',
  'chat.new_placeholder': 'Проект / Отдел…',
  'chat.search_members': 'Поиск участников…',
  'chat.invite_title': 'Пригласить участника',
  'chat.invite_search': 'Поиск участника…',
  'chat.assistant': 'Мой ассистент',
  'chat.ask_assistant': 'Спросить ассистента',
  'chat.ask_assistant_prompt': 'Помоги разобраться с сообщением.\n\nЧат: {title}\nАвтор: {author}\nСообщение:\n{text}',
  'chat.ask_assistant_context': 'Контекст объекта: {type}, таблица «{code}», запись: {id}. При необходимости прочитай запись инструментом read_object.',
  'chat.ask_assistant_sent': 'Запрос отправлен ассистенту',
  'chat.ask_assistant_queued': 'Нет связи: запрос ассистенту в очереди (отправится при восстановлении)',
  'chat.assistant_unknown_author': 'Автор сообщения',
  'chat.assistant_new_dialog': 'Новый диалог',
  'chat.assistant_new_dialog_hint': 'Начать разговор заново (история скрывается, память диалога сбрасывается)',
  'chat.assistant_new_dialog_confirm': 'Начать новый диалог? Текущая переписка будет скрыта, а ассистент забудет контекст этого разговора.',
  'chat.assistant_new_dialog_done': 'Начат новый диалог',
  'chat.assistant_empty_title': 'Мой ассистент',
  'chat.assistant_empty_hint': 'Спросите о данных базы, задачах или заявках, попросите найти информацию. Ассистент работает в пределах ваших прав и помнит контекст разговора.',
  'chat.assistant_suggest_1': 'Что у меня на сегодня?',
  'chat.assistant_suggest_2': 'Какие заявки новые?',
  'chat.assistant_suggest_3': 'Найди пользователя по имени',
  'chat.contacts': 'Контакты',
  'chat.more_actions': 'Действия',
  'chat.favorite': 'В избранное',
  'chat.forwarding': 'Пересылка…',
  'chat.other_empty': 'Других чатов нет',
  'chat.edit_title': 'Изменить сообщение',
  'chat.edit_history': 'История правок сохранится автоматически',
  'chat.cancel': 'Отмена',
  'chat.remove': 'Убрать',
  'chat.attach': 'Прикрепить файлы',
  'chat.geo': 'Отправить геометку',
  'chat.cancel_reply': 'Отменить',
  'chat.cancel_recording': 'Отменить запись (X / Esc)',
  'chat.jump_bottom': 'К последнему сообщению',
  'chat.about': 'О чате',
  'chat.invite_link': 'Пригласить по ссылке',
  'chat.video_call': 'Видеозвонок',
  'chat.end_video_call': 'Завершить видеозвонок',
  'chat.remote': 'Удалённый доступ',
  'chat.remote_show': 'Показать экран',
  'chat.remote_end': 'Завершить сеанс экрана',
  'chat.favorites': 'Избранное',
  'chat.creating': 'создаётся…',
  'chat.discussion': 'Обсуждение',
  'chat.invite': 'Пригласить',
  'chat.dialogs': 'Диалоги',

  // --- Маркетплейс ----------------------------------------------------------
  'market.title': 'Заявки на услуги',
  'market.subtitle': 'Опишите задачу — мастера откликнутся',
  'market.profile': 'Профиль',
  'market.draft': 'Черновик заявки',
  'market.delete_draft': 'Удалить черновик',
  'market.start_here': 'С чего начать',
  'market.recent': 'Последние заявки',
  'market.how': 'Как это работает',
  'market.my_requests': 'Мои заявки',
  'market.new_request': 'Новая заявка',
  'market.leave_request': 'Оставить заявку',
  'market.request': 'Заявка',
  'market.feed': 'Лента заявок',
  'market.wallet': 'Кошелёк мастера',
  'market.wallet_subtitle': 'Баланс, резервы и история',
  'market.operator': 'Кабинет оператора',
  'market.operator_subtitle': 'Мастера, возвраты и архив заявок',
  'market.chat': 'Чат по заявке',
  'market.empty': 'Заявок пока нет',
  'market.no_free': 'Свободных заявок нет',
  'market.not_taken': 'Вы ещё не брали заявки',
  'market.billing_off': 'Биллинг не включён',
  'market.no_access': 'Нет доступа к проверке',
  'market.login_required': 'Требуется вход',
  'market.done': 'Готово',
  'market.photos': 'Фото',
  'market.photos_limit': 'До {count} изображений',
  'market.cancel_request': 'Отменить заявку?',
  'market.refresh': 'Обновить',
  'market.total': 'Всего: {count}',
  'market.masters': 'Мастера',
  'market.select_master': 'Подбор мастера',
  'market.hero_title': 'Расскажите, что нужно сделать',
  'market.hero_text': 'Заявка занимает пару минут. Укажите категорию, адрес и описание — мастера увидят задачу и смогут откликнуться.',
  'market.continue_draft': 'Продолжить заполнение',
  'market.profile_addresses': 'Профиль и мои адреса',
  'market.feed_for_master': 'Лента заявок для мастера',
  'market.categories_loading': 'Загружаем категории…',
  'market.signin_requests': 'Войдите, чтобы видеть свои заявки и переписку с мастерами.',
  'market.all_requests': 'Все заявки',
  'market.recent_loading': 'Загружаем ваши заявки…',
  'market.recent_empty': 'У вас пока нет заявок. Начните с кнопки «Оставить заявку».',
  'market.draft_saved_device': 'Данные сохранены на этом устройстве',
  'market.draft_saved_at': 'Сохранён {date}',
  'market.how1': 'Опишите задачу и укажите адрес — это займёт пару минут.',
  'market.how2': 'Мастера увидят заявку и откликнутся; выбранный мастер появится в карточке.',
  'market.how3': 'Общайтесь в чате заявки и уточняйте детали.',
  'market.requests_loading': 'Загружаем заявки…',
  'market.requests_empty_title': 'Заявок пока нет',
  'market.requests_empty_text': 'Создайте первую заявку — мастера увидят её и смогут откликнуться.',
  'market.free_requests': 'Свободные заявки',
  'market.taken_requests': 'Мои заявки',
  'market.feed_loading_free': 'Загружаем свободные заявки…',
  'market.feed_loading_mine': 'Загружаем ваши заявки…',
  'market.feed_subtitle_free': 'Для мастеров · сначала свежие',
  'market.feed_subtitle_mine': 'Взятые мной заявки',
  'market.wallet_loading': 'Загружаем кошелёк…',
  'market.operator_denied_text': 'Операторский экран доступен роли «Оператор маркетплейса» и администратору базы.',
  'market.masters_loading': 'Загружаем мастеров на проверке…',
  'market.paid_loading': 'Загружаем оплаченные заявки…',
  'market.archive_loading': 'Загружаем архив заявок…',
  'market.review_queue_empty': 'Очередь проверки пуста',
  'market.no_refunds': 'Возвратов пока нет',
  'market.archive_empty': 'Архив пуст',
  'market.service_request': 'Заявка на услугу',
  'market.request_loading': 'Загружаем заявку…',
  'market.step': 'Шаг {step} из {total} · {title}',
  'market.showcase': 'Витрина услуг',
  'market.checking_access': 'Проверяем доступ',
  'market.mode_error': 'Не удалось определить режим',
  'market.back': 'Назад',

  // --- Психология -----------------------------------------------------------
  'psy.title': 'Психолог',
  'psy.app_unavailable': 'Приложение «Психолог» недоступно',
  'psy.app_unavailable_hint': 'Доступ — у роли «Психолог» или «Психолог-стажёр» (назначает администратор базы). Раздел для работы не требуется: все данные — в этом приложении.',
  'psy.cabinet_unavailable': 'Кабинет пациента недоступен',
  'psy.cabinet_unavailable_hint': 'Раздел предназначен системной роли «Пациент» (учётная запись пациента создаётся при привязке карточки клиента и входит по приглашению). Роль назначает специалист/администратор базы.',
  'psy.my_cabinet': 'Мой кабинет',
  'psy.my_data': 'Мои данные',
  'psy.available_tests': 'Доступные тесты',
  'psy.my_results': 'Мои результаты',
  'psy.no_results': 'Результатов пока нет — пройдите тест.',
  'psy.card_by_specialist': 'Карточка заполняется специалистом',
  'psy.loading': 'Загрузка…',
  'psy.date': 'Дата',
  'psy.test': 'Тест',
  'psy.score': 'Балл',
  'psy.passed': 'Пройдено',
  'psy.conclusion': 'Заключение',
  'psy.summary': 'Сводка',
  'psy.sessions': 'Сессии',
  'psy.notes': 'Заметки',
  'psy.conclusions': 'Заключения',
  'psy.tests_results': 'Тесты и результаты',
  'psy.reports': 'Отчёты',
  'psy.patient_cabinet': 'Кабинет пациента',
  'psy.patients': 'Пациенты',
  'psy.new_patient': 'Новый пациент',
  'psy.core_missing': 'Не установлено ядро «ГидраМедик»',
  'psy.core_missing_hint': 'Приложению «Психолог» нужен справочник пациентов ядра (hydra.medic.core). Установите ядро поставкой — рабочие места психолога и пациента станут доступны.',
  'psy.current_user_missing': 'Не определён текущий пользователь — войдите заново',
  'psy.cabinet_loading': 'Загрузка кабинета…',
  'psy.cabinet_mechanism_error': 'Не удалось определить механизм кабинета',
  'psy.retry': 'Повторить',
  'psy.take_test': 'Пройти',
  'psy.no_methods': 'Нет доступных методик — назначьте тест через специалиста.',
  'psy.protocols_denied': 'Просмотр протоколов роли «Пациент» не разрешён — результаты ведёт специалист (право «Чтение» на типы опросов выдаёт администратор базы).',
  'psy.card_denied': 'Карточку клиента ведёт специалист — чтение карточки роли «Пациент» не выдано.',
  'psy.name': 'ФИО',
  'psy.phone': 'Телефон',
  'psy.email': 'Почта',

  'psy.select_patient': 'Выберите пациента',
  'psy.reports_hint': 'Отчёты по методикам и печать «Заключение по тесту»',
  'psy.gender_male': 'мужской',
  'psy.gender_female': 'женский',
  'psy.patient_default': 'Пациент',
  'psy.session_title': 'Сессия',
  'psy.note_title': 'Заметка',
  'psy.conclusion_title': 'Заключение',
  'market.request_canceled': 'Заявка отменена',
  'market.request_taken': 'Заявка взята — контакты открыты',
  'market.service_area': 'Зона обслуживания',
  'market.refund_done': 'Возврат оформлен и записан в журнал',
  'market.request_not_found': 'Заявка не найдена',
  'market.description_missing': 'Описание не указано',
  'market.category': 'Категория',
  'market.address': 'Район и адрес',
  'market.phone': 'Телефон',
  'market.master': 'Мастер',
  'market.master_not_chosen': 'Мастер пока не выбран',
  'market.created': 'Создана',
  'market.add': 'Добавить',
  'market.uploading': 'Отправка {progress}',
  'market.no_photos_hint': 'Фото нет. Добавьте снимок — мастеру будет проще оценить задачу.',
  'market.review_title': 'Отзыв о мастере',
  'market.review_hint': 'Отзыв можно оставить только после завершённой заявки. Один клиент — один отзыв.',
  'market.review_yours': 'Ваш отзыв',
  'market.review_placeholder': 'Расскажите, как прошла работа',
  'market.review_submit': 'Оставить отзыв',
  'market.review_sending': 'Отправка…',
  'market.review_thanks': 'Спасибо! Отзыв опубликован.',
  'market.review_rating': 'Ваша оценка',
  'market.review_list_empty': 'Отзывов пока нет — станьте первым.',
  'market.review_loading': 'Загружаем отзывы…',

  'admin.users.title': 'Пользователи',
  'admin.users.search': 'Поиск: имя, логин, email…',
  'admin.users.create': 'Создать пользователя',
  'admin.users.qr': 'QR-код входа',
  'admin.users.not_found': 'Никого не найдено',
  'admin.users.no_roles': 'нет ролей',
  'admin.users.system_badge': 'СИСТ.',
  'admin.users.system_hint': 'Системная учётная запись ядра',
  'admin.users.new': 'Новый пользователь',
  'admin.users.login_required': 'Логин (обязательно)',
  'admin.users.name': 'Имя',
  'admin.users.password_hint': 'Пароль (пусто = changeme)',
  'admin.users.select': 'Выберите пользователя из списка',
  'admin.users.created': 'Пользователь создан',
  'admin.users.load_error': 'Ошибка загрузки пользователей',
  'admin.users.create_error': 'Ошибка создания',
  'admin.users.devices_load_error': 'Ошибка загрузки устройств',
  'admin.users.settings_load_error': 'Ошибка загрузки настройки',
  'admin.users.sms_allowed': 'СМС-шлюз разрешён для устройств пользователя',
  'admin.users.sms_denied': 'СМС-шлюз запрещён для устройств пользователя',
  'admin.users.sms_save_error': 'Не удалось сохранить настройку',
  'admin.users.request_location': 'Запросить координаты',
  'admin.users.request_location_title': 'Запрос геопозиции',
  'admin.users.request_location_body': 'Администратор запросил ваши координаты. Откройте приложение.',
  'admin.users.location_sent': 'Запрос координат отправлен пользователю',
  'admin.users.location_error': 'Ошибка отправки запроса',
  'admin.users.sessions_empty': 'История входов пуста',
  'admin.users.session_failed': 'неудачный вход',
  'admin.users.session_login': 'вход',
  'admin.users.created_via.admin': 'Администратор',
  'admin.users.created_via.self_registration': 'Саморегистрация',
  'admin.users.created_via.auto': 'Автосоздание',
  'admin.users.created_via.system': 'Системная',
  'admin.users.created_via.qr': 'QR-код',

  'admin.roles.title': 'Роли и разрешения',
  'admin.roles.default_for_new': 'Новые пользователи получают: {name}',
  'admin.roles.create': 'Создать роль',
  'admin.roles.none': 'Ролей нет',
  'admin.roles.select': 'Выберите роль из списка',
  'admin.roles.name_placeholder': 'Имя роли',
  'admin.roles.desc_placeholder': 'Описание роли',
  'admin.roles.is_default': 'по умолчанию',
  'admin.roles.system': 'системная',
  'admin.roles.full_access': 'полные права',
  'admin.roles.set_default': 'По умолчанию',
  'admin.roles.set_default_hint': 'Новые пользователи будут получать эту роль автоматически',
  'admin.roles.default_toast': 'Роль «{name}» — по умолчанию для новых пользователей',
  'admin.roles.save_name': 'Сохранить имя и описание',
  'admin.roles.tab.perms': 'Разрешения',
  'admin.roles.tab.users': 'Пользователи',
  'admin.roles.tab.users_count': 'Пользователи ({count})',
  'admin.roles.tab.permissions': 'Полномочия',
  'admin.roles.system_hint': 'Системная роль — разрешения заданы платформой.',
  'admin.roles.perms_hint': 'Разрешения роли (сохраняются кнопкой).',
  'admin.roles.custom_placeholder': 'Произвольное разрешение, например reports:view',
  'admin.roles.save_perms': 'Сохранить разрешения',
  'admin.roles.no_users': 'В роли пока нет пользователей',
  'admin.roles.add_user': 'Добавить пользователя',
  'admin.roles.remove_role': 'Убрать роль',
  'admin.roles.copy_hint': 'Копировать роль',
  'admin.roles.delete_hint': 'Удалить роль',
  'admin.roles.created': 'Роль создана',
  'admin.roles.saved': 'Роль сохранена',
  'admin.roles.deleted': 'Роль удалена',
  'admin.roles.copied': 'Роль скопирована с разрешениями',
  'admin.roles.perms_saved': 'Разрешения сохранены',
  'admin.roles.user_added': 'Пользователь добавлен в роль',
  'admin.roles.load_error': 'Ошибка загрузки ролей',
  'admin.roles.create_error': 'Ошибка создания роли',
  'admin.roles.update_error': 'Ошибка обновления роли',
  'admin.roles.delete_error': 'Ошибка удаления роли',
  'admin.roles.copy_error': 'Ошибка копирования роли',
  'admin.roles.perms_error': 'Ошибка сохранения разрешений',
  'admin.roles.error': 'Ошибка',
  'admin.roles.confirm_delete': 'Удалить роль «{name}»?',
  'admin.roles.new_name': 'Новая роль',
  'admin.roles.copy_suffix': ' (копия)',
  'admin.roles.perm.full': 'Полные права',
  'admin.roles.perm.full_desc': 'Доступ ко всем объектам и операциям',
  'admin.roles.perm.audit': 'Просмотр истории изменений',
  'admin.roles.perm.audit_desc': 'История изменений объектов (audit log)',
  'admin.roles.perm.task_edit': 'Изменение созданных задач',
  'admin.roles.perm.task_edit_desc': 'Изменение/удаление любых задач (автор и исполнитель не могут)',

  'app.loading': 'Загрузка…',
  'app.offline_short': 'Офлайн-режим: данные из кэша, синхронизация при появлении сети',
  'app.offline_long': 'Офлайн-режим: сервер недоступен. Данные из кэша, запись сохранится и синхронизируется при восстановлении связи.',
  'app.connect_error': 'Ошибка подключения к базе: {error}',
  'app.push_title': 'Включить уведомления?',
  'app.push_body': 'Оповещения о новых сообщениях и задачах, даже когда приложение закрыто.',
  'app.tracking_title': 'Отслеживание координат',
  'app.tracking_body': 'Политика вашей роли требует отслеживания координат. Включить?',
  'app.enable': 'Включить',
  'app.not_now': 'Не сейчас',
  'app.close': 'Закрыть',

  'chat.reply_action': 'Ответить',
  'chat.reaction_action': 'Реакция',
  'chat.forward_action': 'Переслать',
  'chat.edit_action': 'Изменить',
  'chat.delete_action': 'Удалить',
  'chat.select_action': 'Выбрать',
  'chat.hide_one_confirm': 'Скрыть выбранное сообщение у себя? Восстановить будет нельзя.',
  'chat.hide_selected_confirm': 'Скрыть выбранные сообщения у себя ({count})? Восстановить будет нельзя.',
  'chat.hide_single_confirm': 'Скрыть сообщение у себя? Восстановить будет нельзя.',
  'chat.voice_load_error': 'Не удалось загрузить голосовое сообщение',
  'chat.no_files': 'Нет файлов для отправки',
  'chat.empty_file': 'файл пуст',
  'chat.choose_participant': 'Выберите участника',
  'chat.open_action': 'Открыть',
  'chat.pin': 'Закрепить',
  'chat.unpin': 'Открепить',
  'chat.mute': 'Беззвучный',
  'chat.unmute': 'Со звонком',
  'chat.to_archive': 'В архив',
  'chat.from_archive': 'Из архива',
  'chat.hide_chat': 'Скрыть чат',
  'chat.show_in_list': 'Показать в списке',
  'chat.context_discussion': 'Обсуждение',
  'chat.context_chat': 'Чат',

  'chat.selected_count': 'Выбрано: {count}',
  'chat.select_messages': 'Выберите сообщения',
  'chat.copy_action': 'Копировать',
  'chat.cancel_hint': 'Отмена (Esc)',
  'chat.exit_select': 'Выйти из режима выбора',
  'chat.copy_hint': 'Копировать тексты выбранных сообщений',
  'chat.forward_hint': 'Переслать выбранные сообщения',
  'chat.hide_selected_hint': 'Скрыть выбранные сообщения у себя',
  'chat.selected': 'Выбрано',
  'chat.not_selected': 'Не выбрано',
  'chat.forward_title': 'Переслать сообщение',
  'chat.forward_many': 'Переслать сообщения ({count})',
  'chat.reply_send': 'Ответить',
  'chat.saving': 'Сохраняем…',
  'chat.forwarding_prefix': '➡️ Переслать: {label}',

  'common.copy': 'Копировать',
  'common.copied': 'Скопировано',
  'notification.default_title': 'Уведомление',
  'form.file_info_error': 'Ошибка загрузки сведений о файле',
  'form.geo_unavailable': 'Координаты недоступны — файл загружен без них',
  'form.file_upload_error': 'Ошибка загрузки файла',
  'form.file_download_error': 'Ошибка скачивания файла',
  'form.file_preview_error': 'Ошибка предпросмотра файла',
  'form.file_uploaded': 'файл загружен',
  'form.download': 'Скачать',
  'form.preview': 'Предпросмотр',
  'app.session_unavailable': 'Сервер недоступен: {target}. Сохранённой сессии для этой базы нет — офлайн-режим недоступен. Войдите, когда сервер будет доступен.',
  'app.local_server': 'локальный',
  'app.admin_login_failed': 'Авторизация admin/admin не удалась',

  'chat.clear_self_confirm': 'Вы действительно хотите очистить чат у себя?',
  'chat.clear_all_confirm': 'Вы действительно хотите удалить все сообщения?',
  'chat.clear_permanent_confirm': 'Удалить все сообщения окончательно (без восстановления)?',
  'chat.me': 'Я',
  'chat.offline_status': 'не в сети',
  'form.voice_unavailable': 'Распознавание речи недоступно (нужен Chrome/Edge с микрофоном).',
  'form.voice_error': 'Ошибка распознавания',
  'form.voice_start_error': 'Не удалось запустить распознавание',
  'form.voice_input': 'Голосовой ввод',
  'market.master_login_needed': 'Войдите в систему, чтобы брать заявки.',
  'market.master_offline_take': 'Нет связи с сервером: взятие заявки (списание денег) недоступно до восстановления связи.',
  'market.master_billing_off': 'Биллинг выключен в базе — взятие заявок за плату недоступно.',
  'market.sync_error': 'Не удалось применить изменение заявки — сверяемся с сервером…',
  'market.master_confirmed': 'Мастер подтверждён',
  'market.master_rejected': 'Мастер отклонён — причина сохранена',
  'market.decision_saved': 'Решение уже было сохранено — мастер не изменился',
  'market.search_review': 'Имя, телефон, почта или причина',
  'market.search_archive': 'Номер, описание, адрес или мастер',
  'market.review_edit_hint': 'Измените запрос — поиск идёт по имени, контактам и причине отказа.',
  'market.review_empty': 'Все мастера проверены. Новые заявки появятся здесь автоматически.',
  'market.user_missing': 'Не определён текущий пользователь — войдите заново',

  // --- Общее ----------------------------------------------------------------
  'common.cancel': 'Отмена',
  'common.save': 'Сохранить',
  'common.close': 'Закрыть',
  'common.refresh': 'Обновить',
  'common.add': 'Добавить',
  'common.loading': 'Загрузка…',
  'common.retry': 'Повторить',
  'common.error': 'Ошибка',
  'common.no_data': 'Нет данных',
  'common.create': 'Создать',
  'common.edit': 'Редактирование',
  'common.creating': 'Создание',
  'common.delete': 'Удалить',
  'common.saving': 'Сохранение...',
  'common.saved': 'Сохранено',
  'common.ok': 'OK',
  'common.yes': 'Да',
  'common.no': 'Нет',
  'common.more': 'Ещё',
  'common.apply': 'Применить',
  'common.clear': 'Очистить',
  'common.reset': 'Сбросить',
  'common.search': 'Поиск',
  'common.required': 'обязательно',
  'common.search_placeholder': 'Поиск…',
  'common.clear_search': 'Очистить поиск',
  'common.voice_search': 'Голосовой поиск',
  'common.stop_listening': 'Остановить распознавание',
  'common.remove': 'Убрать',
  'common.copy_message': 'Скопировать сообщение',
  'common.close_notification': 'Закрыть уведомление',
  'common.done': 'Выполнено',
  'common.canceled': 'Отменено',
  'common.optional': 'необязательно',

  // --- Ошибки и диалоги -----------------------------------------------------
  'errors.network_unavailable': 'Сервер недоступен. Проверьте подключение к серверу и повторите попытку.',
  'dialog.title.confirm': 'Подтверждение',
  'dialog.title.message': 'Сообщение',
  'dialog.title.input': 'Ввод',
  'dialog.cancel': 'Отмена',
  'dialog.yes': 'Да',
  'dialog.ok': 'OK',

  // --- Аудит и статусы ------------------------------------------------------
  'audit.created_by': 'Создал',
  'audit.created_at': 'Дата создания',
  'audit.updated_by': 'Изменил',
  'audit.updated_at': 'Дата изменения',
  'audit.status_changed': 'Статус изменён',
  'status.label': 'Статус:',
  'status.no_transitions': 'Нет доступных переходов',
  'status.choose': 'Выбрать статус',
  'status.choose_title': 'Выбор статуса',
  'status.changing': 'смена статуса…',
  'status.changed': 'Статус изменён',

  // --- Период списка --------------------------------------------------------
  'period.title': 'Период (по дате создания)',
  'period.quick': 'Быстрый выбор',
  'period.today': 'Сегодня',
  'period.yesterday': 'Вчера',
  'period.last7': 'Последние 7 дней',
  'period.last30': 'Последние 30 дней',
  'period.this_week': 'Эта неделя',
  'period.this_month': 'Этот месяц',
  'period.prev_month': 'Прошлый месяц',
  'period.this_quarter': 'Этот квартал',
  'period.this_year': 'Этот год',
  'period.from': 'С даты',
  'period.to_inclusive': 'По дату (включительно)',
  'period.hint': 'Отбор применяется ко всем записям списка по полю «Дата создания» и сохраняется для вас (настройка списка + пользователя). «По дату» включает выбранный день целиком.',
  'period.clear': 'Очистить поля',
  'period.reset': 'Сбросить период',
  'period.reset_hint': 'Убрать отбор по периоду',

  // --- Настройки списка -----------------------------------------------------
  'list_settings.title': 'Настройки списка',
  'list_settings.tab.group': 'Группировки',
  'list_settings.tab.filters': 'Отборы',
  'list_settings.mode.label': 'Вариант отображения',
  'list_settings.mode.none': 'Без группировки',
  'list_settings.mode.sections': 'Секции (сворачиваемые)',
  'list_settings.mode.kanban': 'Канбан (колонки)',
  'list_settings.mode.chips': 'Чипы-итоги (фильтр)',
  'list_settings.mode.cards': 'Карточки-группы (с провалом)',
  'list_settings.fields.label': 'Поля группировки (порядок = вложенность: для «карточек-групп» клик ведёт к следующему уровню, затем к объектам)',
  'list_settings.fields.empty': 'Поля не выбраны — список без группировки.',
  'list_settings.fields.add': '+ Добавить поле группировки…',
  'list_settings.example': 'Пример вложенности: Статус → Год → Месяц → День: выберите «Статус», затем «Год · Дата создания», «Месяц · …», «День · …» и вариант «Карточки-группы».',
  'list_settings.filters.hint': 'Отборы применяются к списку ВМЕСТЕ с отборами формы/виджета (AND) и сохраняются для вас (настройки «список + пользователь»). Пример ПЕРИОДА: по полю даты добавьте ДВА условия — «больше или равно» (с) и «меньше или равно» (по); даты в формате ГГГГ-ММ-ДД. Символ @current_user доступен в значении.',
  'list_settings.reset': 'Сбросить к настройкам формы',
  'grouping.year': 'Год',
  'grouping.month': 'Месяц',
  'grouping.day': 'День',
  'grouping.move_up': 'Выше',
  'grouping.move_down': 'Ниже',

  // --- Команды формы --------------------------------------------------------
  'form.no_rights_edit': 'Нет прав на изменение',
  'form.no_rights_create': 'Нет прав на добавление',
  'form.no_rights_hint': 'Права объекта заданы в конфигураторе (вкладка «Права»)',
  'form.posting': 'Проведение...',
  'form.post_and_close': 'Провести и закрыть',
  'form.post': 'Провести',
  'form.unpost': 'Отменить проведение',
  'form.camera_hint': 'Снять с камеры и прикрепить к объекту',
  'form.discussion': 'Обсуждение',
  'form.choose_value': 'Выбрать значение',
  'form.choose_type_title': 'Выбор типа',
  'form.choose_type_hint': 'Выберите тип значения',
  'form.no_types': 'Типов нет',
  'form.choose_value_title': 'Выбор значения',
  'form.no_file': 'Файл не выбран',
  'form.choose_file': 'Выбрать файл',
  'form.camera': 'Снять с камеры',
  'form.transfer': 'Передать…',
  'form.restore': 'Восстановить',
  'form.delete_permanent': 'Удалить окончательно',
  'form.upload_files': 'Загрузить файлы',
  'form.tab.main': 'Основное',
  'form.tab.tasks': 'Задачи',
  'form.tab.files': 'Файлы',
  'form.title': '{name} — {action}',
  'form.error.load': 'Ошибка загрузки',
  'form.error.save': 'Ошибка сохранения',
  'form.error.post': 'Ошибка проведения',
  'form.error.unpost': 'Ошибка отмены проведения',
  'form.error.file_load': 'Ошибка загрузки файла',
  'form.error.file_download': 'Ошибка скачивания файла',
  'form.error.delete': 'Ошибка удаления',
  'form.error.tabular_save': 'Ошибка сохранения табличной части «{name}»',
  'form.error.restore': 'Ошибка восстановления',
  'form.error.status': 'Ошибка смены статуса',
  'form.transfer.done': 'Задача передана исполнителю',
  'form.transfer.created': 'Задача создана и назначена исполнителю',
  'form.transfer.no_task_type': 'Не найден тип задач для назначения исполнителя',
  'form.comment_title': 'Комментарий ({action})',
  'form.delete_permanent_confirm': 'Удалить окончательно? Запись будет удалена безвозвратно',
  'form.mark_order_delete_confirm': 'Пометить заявку на удаление? Связанные задачи также будут помечены',
  'form.mark_record_delete_confirm': 'Пометить запись на удаление?',
  'form.task_subject_required': 'Сначала сохраните заявку — предмет задачи нужен её id',
  'form.new_group': 'Новая группа',
  'form.type.task': 'Задача',
  'form.type.user': 'Пользователь',
  'form.type.file': 'Файл',
  'form.default_order': 'Заявка',
  'form.default_order_number': 'Заявка №{number}',

  // --- Команды списков ------------------------------------------------------
  'list.create': 'Создать',
  'list.refresh': 'Обновить',
  'list.create_group': 'Добавить группу',
  'list.cancel': 'Отмена',
  'list.save_filter_folder': 'Сохранить отбор как папку',
  'list.show_marked': 'Показывать помеченные',
  'list.settings': 'Настройки',
  'list.print': 'Печать',
  'list.period': 'Период',
  'list.expand': 'Развернуть',
  'list.collapse': 'Свернуть',
  'list.show_more': 'Показать ещё',
  'list.confirm_post': 'Провести {count} документ(ов)?',
  'list.confirm_mark_delete': 'Пометить на удаление {count} записей?',
  'list.confirm_unpost': 'Отменить проведение {count} документ(ов)?',
  'list.error.post': 'Ошибка проведения',
  'list.error.unpost': 'Ошибка отмены проведения',
  'list.error.group': 'Ошибка создания группы',
  'list.error.folder': 'Ошибка сохранения папки',
  'list.folder.user': 'Личная',
  'list.folder.system': 'Системная',
  'list.folder.shared': 'Общая',
  'list.save_folder_title': 'Сохранить отбор как папку',
  'list.save_folder_name': 'Название',
  'list.save_folder_placeholder': 'Например: Мои задачи на неделю',
  'list.save_folder_existing': 'Существующие папки',
  'list.save_folder_kind': 'Вид папки',
  'list.save_folder_save': 'Сохранить',
  'list.mark_delete': 'Пометить на удаление ({count})',
  'list.delete_permanent': 'Удалить окончательно ({count})',
  'list.post_count': 'Провести ({count})',
  'list.unpost_count': 'Отменить проведение ({count})',
  'list.active_filter': 'Отбор активен: {count}',
  'list.all': 'Все',
  'list.shared_folder': 'Общая папка (видна всем пользователям)',
  'list.save_folder_hint': 'Сохранит текущие отборы ({count}) как папку этого списка. @current_user в отборах подставляется автоматически для каждого пользователя.',
  'grouping.empty': '(пусто)',
  'list.column.owner': 'Владелец',
  'list.column.parent': 'Родитель',
  'list.column.group': 'Группа',
  'list.column.marked': 'Пометка удаления',
  'list.column.created_by': 'Создатель',
  'list.column.updated_by': 'Изменил',
  'list.column.created_at': 'Дата создания',
  'list.column.updated_at': 'Дата изменения',
  'list.column.status': 'Статус',
  'list.column.active': 'Активных',
  'list.column.files': 'Файлы',
  'list.column.login': 'Логин',
  'list.column.name': 'Наименование',
  'list.column.email': 'Электронная почта',
  'list.column.description': 'Описание',

  // --- Мессенджер (остатки) -------------------------------------------------
  'chat.offline_reply': 'Офлайн: ответ уйдёт при восстановлении связи',
  'chat.forwarded_favorites': 'Переслано в «Избранное»',
  'chat.copied_count': 'Скопировано сообщений: {count}',
  'chat.voice_unavailable': 'Голосовые сообщения недоступны в этом браузере (нужен микрофон и Chrome/Edge).',
  'chat.recording': 'Запись голосового сообщения',
  'chat.voice_offline_queued': 'Офлайн: голосовое будет отправлено при восстановлении связи',
  'chat.transcript_show_full': 'Показать полностью',
  'chat.transcript_collapse': 'Свернуть',
  'chat.edit_transcript_title': 'Изменить текст расшифровки',
  'voice.recognizing': 'Распознаю',
  'chat.offline_send': 'Офлайн: сообщение будет отправлено при восстановлении связи',
  'chat.offline_send_attachments': 'Офлайн: сообщение с вложениями будет отправлено при восстановлении связи',
  'chat.offline_geo': 'Офлайн: геометка будет отправлена при восстановлении связи',
  'chat.message_not_found': 'Сообщение не найдено в истории (возможно, оно удалено)',
  'chat.dialog_type': 'Тип',
  'chat.dialog_direct': 'Диалог',
  'chat.dialog_group': 'Группа',
  'chat.group_name': 'Название группы',
  'chat.group_hint': 'Группу можно создать БЕЗ участников — вы добавитесь автоматически (участников приглашайте позже кнопкой «Пригласить»).',
  'chat.members': 'Участники',
  'chat.create': 'Создать',
  'chat.member_not_in_discussion': 'Участник (не состоящий в обсуждении)',
  'chat.autoplay': 'Слушать подряд',
  'chat.autoplay_on': '«Слушать подряд»: включено — следующее голосовое запустится автоматически',
  'chat.autoplay_off': '«Слушать подряд»: выключено',
  'chat.autoplay_on_hint': 'Слушать подряд: включено (следующее голосовое — автоматически)',
  'chat.autoplay_off_hint': 'Слушать подряд: выключено',

  // --- ЭКГ: находки MI/HYP --------------------------------------------------
  'ecg.mi.badge.screening': 'Скрининг: подозрение на инфаркт',
  'ecg.mi.badge.confirmed': 'Вероятный инфаркт (подтверждено правилами ST)',
  'ecg.mi.text.screening': 'Подозрение на инфаркт (скрининг). Требуется подтверждение. Не диагноз.',
  'ecg.mi.text.confirmed': 'Вероятный инфаркт по ЭКГ-критериям. Не диагноз.',
  'ecg.mi_hyp.abstain': 'Противоречивые признаки инфаркта и гипертрофии — требуется оценка врача.',
  'ecg.st.prefix': 'ST:',
  'ecg.st.elevation': 'элевация',
  'ecg.st.depression': 'депрессия',
  'ecg.st.mv': 'мВ',
  'ecg.st.none': 'ST: без значимых смещений',
  'ecg.st.unavailable': 'ST-критерии недоступны',
  'ecg.hyp.title': 'ЭКГ-признаки гипертрофии',
  'ecg.hyp.text': 'Требуется ЭхоКГ. Не диагноз.',
  'ecg.hyp.no_criteria': 'амплитудные критерии не выполнялись',
  'ecg.hyp.sokolow': 'Sokolow-Lyon',
  'ecg.hyp.cornell': 'Cornell',
  'ecg.hyp.peguero': 'Peguero-Lo Presti',
  'ecg.hyp.rvh': 'RVH',
  'ecg.hyp.source_label': 'источник: {source}',
  'ecg.hyp.source.nn': 'модель',
  'ecg.hyp.source.both': 'модель и критерии',
  'ecg.hyp.source.rules': 'только критерии',
  'ecg.hyp.source.conflict': 'модель и критерии расходятся',
  'ecg.conflict': 'Противоречивые признаки — требуется оценка врача.',

  // --- Телефон: подтверждение номера ----------------------------------------
  'phone.title': 'Телефон',
  'phone.status_verified': 'Номер подтверждён',
  'phone.not_verified': 'Номер не подтверждён',
  'phone.only_last4': 'только последние 4 цифры',
  'phone.edit': 'Изменить номер',
  'phone.saving': 'Сохранение…',
  'phone.save': 'Сохранить',
  'phone.cancel': 'Отмена',
  'phone.choose_title': 'Как подтвердить номер',
  'phone.no_methods': 'Сейчас нет доступных способов. Обратитесь к администратору базы.',
  'phone.sending': 'Отправляем запрос…',
  'phone.wait_title': 'Откройте Telegram и подтвердите номер',
  'phone.link_until': 'Ссылка действует до {time}',
  'phone.remaining': 'осталось {seconds} с',
  'phone.open_telegram': 'Открыть Telegram',
  'phone.open_max': 'Открыть MAX',
  'phone.done': 'Подтверждено',
  'phone.done_text': 'Номер +• (•••) ••• {last4} подтверждён через Telegram.',
  'phone.done_text_max': 'Номер +• (•••) ••• {last4} подтверждён через MAX.',
  'phone.done_not_linked': 'Отметка в профиле пока не обновилась: подтверждение получено центром, но система ещё не связала его с учётной записью. Сообщите администратору — данные не потеряны.',
  'phone.retry': 'Повторить',
  'phone.close': 'Закрыть',
  'phone.code_sent_telegram': 'Код отправлен в Telegram',
  'phone.code_sent_max': 'Код отправлен в MAX',
  'phone.code_sent_sms': 'Код отправлен сообщением',
  'phone.code_sent': 'Код отправлен',
  'phone.operator_pending': 'Заявка у специалиста — подтверждение появится здесь автоматически. Можно выбрать другой способ.',
  'phone.code_enter_below': 'введите его ниже',
  'phone.expires_in': 'Действует ещё {seconds} с',
  'phone.code_placeholder': 'Код подтверждения',
  'phone.checking': 'Проверка…',
  'phone.confirm': 'Подтвердить',
  'phone.confirm_number': 'Подтвердить номер',
  'phone.choose_another': 'Заявка у специалиста — выбрать другой способ',
  'phone.load_failed': 'Не удалось получить состояние номера',
  'phone.trust_confirmed': 'Номер подтверждён в центре подтверждения',
  'phone.expired': 'Срок подтверждения истёк — запросите новую ссылку',
  'phone.rejected': 'Подтверждение отклонено — можно попробовать снова',
  'phone.state_failed': 'Не удалось получить состояние подтверждения',
  'phone.enter_phone': 'Укажите номер телефона',
  'phone.saved_hint': 'Номер сохранён. Подтвердите его — после смены номера подтверждение сбрасывается.',
  'phone.save_failed': 'Не удалось сохранить номер',
  'phone.set_phone_first': 'Сначала укажите и сохраните номер телефона.',
  'phone.base_unknown': 'Не удалось определить базу — откройте профиль из рабочего места базы',
  'phone.telegram_failed': 'Не удалось отправить запрос в Telegram',
  'phone.sent': 'Запрос отправлен',
  'phone.request_failed': 'Не удалось отправить подтверждение',
  'phone.code_required': 'Введите код подтверждения',
  'phone.verified_remembered': 'Номер подтверждён — этот вход запомнен',
  'phone.confirmed': 'Номер подтверждён',
  'phone.code_invalid': 'Неверный или просроченный код',
  'phone.new_number_rejected': 'Новый номер отклонён — доступ по подтверждённому номеру сохранён',
  'phone.reject_failed': 'Не удалось отклонить новый номер',
  'phone.loading': 'Проверяем номер телефона…',
  'phone.verified_on': 'Подтверждён {date}.',
  'phone.new_number_title': 'Мы видим новый номер {title}',
  'phone.new_number_text': 'Он отличается от подтверждённого в профиле. Подтвердите его кодом из бота — профиль не изменится, номер станет дополнительным.',
  'phone.not_my_number': 'Это не мой номер',
  'phone.privacy_note': 'Подтверждение приходит в выбранный вами способ — номер не публикуется.',
  'phone.refresh': 'Обновить',
  'phone.method.telegram': 'Telegram',
  'phone.method.telegram_hint': 'Откроется Telegram — подтвердите номер одним нажатием',
  'phone.method.max': 'MAX',
  'phone.method.max_hint': 'Откроется MAX — подтвердите номер одним нажатием',
  'trust.confirmed': 'Телефон подтверждён',
  'trust.max.unavailable': 'MAX не запущен. Откройте подтверждение из приложения MAX.',
  'trust.max.ready_text': 'Нажмите кнопку и отправьте свой контакт в MAX. Номер уйдёт в центр подтверждения в подписанном виде.',
  'trust.max.init_data_missing': 'MAX не передал подписанные данные (initData). Откройте страницу из бота MAX.',
  'phone.method.device': 'На другом устройстве',
  'phone.method.telegram_device_hint': 'Код придёт в ваш Telegram — введите его здесь',
  'phone.method.max_device_hint': 'Код придёт в ваш MAX — введите его здесь',
  'phone.method.sms': 'СМС',
  'phone.method.sms_hint': 'Код придёт сообщением на номер',
  'phone.method.operator': 'Через специалиста',
  'phone.method.operator_hint': 'Специалист подтвердит номер — статус обновится здесь',
  'phone.method.other': 'Другой способ',

  // --- Обновление фронта (PWA) ----------------------------------------------
  'update.new_version_available': 'Вышла новая версия — перезагрузите приложение',
  'update.reload': 'Перезагрузить',
  'update.auto_applying': 'Применяем обновление — перезагрузка…',
};

const en: Messages = {
  // --- Shell / navigation ---------------------------------------------------
  'nav.start': 'Start',
  'nav.start_hint': 'Start (profile, search, sections, apps, sign out)',
  'nav.worktable': 'Worktable',
  'nav.invite': 'Invite',
  'nav.sections': 'Sections',
  'nav.chat': 'Chat',
  'nav.assistant': 'My assistant',
  'nav.files': 'Files',
  'nav.mail': 'Mail',
  'nav.support': 'Support',
  'nav.cabinet': 'Cabinet',
  'nav.patient_cabinet': 'My cabinet',
  'nav.medic_appointments': 'Book an appointment',
  'nav.voice_rooms': 'Voice rooms',
  'nav.back': 'Back',
  'nav.enterprise': 'Enterprise',
  'nav.contour': 'offline',
  'nav.contour_hint': 'Closed environment: external services disabled, local notifications only',
  'nav.search_sections': 'Search',
  'nav.loading_sections': 'Loading sections…',
  'nav.sections_error': 'Sections failed to load. Click to retry.',
  'nav.nothing_found': 'Nothing found',
  'nav.logout': 'Sign out',
  'nav.profile': 'Profile',
  'nav.profile_settings': 'Profile and settings',
  'nav.user': 'User',
  'nav.connection_online': 'Connection active',
  'nav.connection_reconnecting': 'Reconnecting…',
  'nav.connection_offline': 'No network connection',
  'nav.offline_mode': 'Offline mode (work without server)',

  // --- Settings -------------------------------------------------------------
  'settings.title': 'Settings',
  'settings.done': 'Done',
  'settings.tab.profile': 'Profile',
  'settings.tab.assistant': 'My assistant',
  'settings.tab.language': 'Language',
  'settings.tab.devices': 'Sign-ins',
  'settings.tab.links': 'Linked accounts',
  'settings.tab.privacy': 'Privacy',
  'settings.tab.appearance': 'Appearance',
  'settings.tab.themes': 'Themes',
  'settings.tab.notifications': 'Notifications',
  'settings.tab.app': 'Application',
  'settings.menu.assistant': 'My assistant',
  'settings.menu.appearance': 'Appearance',
  'settings.menu.themes': 'Themes',
  'settings.menu.permissions': 'Permissions',
  'settings.menu.notifications': 'Notifications',
  'settings.menu.app': 'Application',
  'settings.menu.account': 'My account',
  'settings.menu.invite': 'Invite',
  'settings.menu.about': 'About',
  'settings.about.platform': 'Business application platform',
  'settings.about.configuration': 'Configuration',
  'settings.about.version': 'Platform version',
  'settings.about.close': 'Close',
  'settings.language.title': 'Interface language',
  'settings.language.hint': 'Choose a language. If it has no translation yet, texts stay in the base language.',
  'settings.language.default': 'Base language: {locale}',
  'settings.language.fallback': 'No translation yet — showing: {locale}',
  'settings.language.current': 'Current language',
  'settings.language.saved': 'Language saved',
  'settings.language.offline': 'Language saved locally; it will sync when the connection is restored.',

  // --- Chat -----------------------------------------------------------------
  'chat.tab.dialogs': 'Chats',
  'chat.tab.discussions': 'Discussions',
  'chat.tab.rooms': 'Rooms',
  'chat.title': 'Chats',
  'chat.new': 'New chat',
  'chat.group': 'Group',
  'chat.search': 'Search chats…',
  'chat.search_messages': 'Search messages',
  'chat.search_messages_hint': 'Search messages (all chats and filters)',
  'chat.select': 'Select a chat on the left or create a new one',
  'chat.empty': 'No chats yet. Create a new chat.',
  'chat.empty_archive': 'The archive is empty.',
  'chat.empty_discussions': 'No discussions yet. Open a discussion on an object (💬 button in the form).',
  'chat.pinned': 'Pinned',
  'chat.archive': 'Archive ({count})',
  'chat.back_to_dialogs': '← Back to chats',
  'chat.no_messages': 'No messages',
  'chat.loading': 'Loading…',
  'chat.message': 'Message…',
  'chat.reply': 'Reply in discussion…',
  'chat.no_replies': 'No replies yet — start the discussion',
  'chat.hide_self': 'For me',
  'chat.hide_self_hint': 'Hide messages for me',
  'chat.hide_all': 'For everyone',
  'chat.hide_all_hint': 'Mark messages as deleted for everyone',
  'chat.permanent': 'Permanently',
  'chat.permanent_hint': 'Permanently delete all messages',
  'chat.new_title': 'New chat',
  'chat.new_placeholder': 'Project / Department…',
  'chat.search_members': 'Search members…',
  'chat.invite_title': 'Invite member',
  'chat.invite_search': 'Search member…',
  'chat.assistant': 'My assistant',
  'chat.ask_assistant': 'Ask the assistant',
  'chat.ask_assistant_prompt': 'Help me understand this message.\n\nChat: {title}\nAuthor: {author}\nMessage:\n{text}',
  'chat.ask_assistant_context': 'Object context: {type}, table "{code}", record: {id}. Read the record with read_object if needed.',
  'chat.ask_assistant_sent': 'Request sent to the assistant',
  'chat.ask_assistant_queued': 'No connection: the assistant request is queued (it will be sent when back online)',
  'chat.assistant_unknown_author': 'Message author',
  'chat.assistant_new_dialog': 'New dialog',
  'chat.assistant_new_dialog_hint': 'Start over (history is hidden and the dialog memory is reset)',
  'chat.assistant_new_dialog_confirm': 'Start a new dialog? The current conversation will be hidden and the assistant will forget this conversation context.',
  'chat.assistant_new_dialog_done': 'New dialog started',
  'chat.assistant_empty_title': 'My assistant',
  'chat.assistant_empty_hint': 'Ask about the base data, tasks or requests, or ask to find information. The assistant acts within your permissions and remembers the conversation.',
  'chat.assistant_suggest_1': 'What do I have today?',
  'chat.assistant_suggest_2': 'Which requests are new?',
  'chat.assistant_suggest_3': 'Find a user by name',
  'chat.contacts': 'Contacts',
  'chat.more_actions': 'Actions',
  'chat.favorite': 'To favorites',
  'chat.forwarding': 'Forwarding…',
  'chat.other_empty': 'No other chats',
  'chat.edit_title': 'Edit message',
  'chat.edit_history': 'Edit history is saved automatically',
  'chat.cancel': 'Cancel',
  'chat.remove': 'Remove',
  'chat.attach': 'Attach files',
  'chat.geo': 'Send location',
  'chat.cancel_reply': 'Cancel',
  'chat.cancel_recording': 'Cancel recording (X / Esc)',
  'chat.jump_bottom': 'Jump to latest message',
  'chat.about': 'About chat',
  'chat.invite_link': 'Invite by link',
  'chat.video_call': 'Video call',
  'chat.end_video_call': 'End video call',
  'chat.remote': 'Remote access',
  'chat.remote_show': 'Share screen',
  'chat.remote_end': 'End screen session',
  'chat.favorites': 'Favorites',
  'chat.creating': 'creating…',
  'chat.discussion': 'Discussion',
  'chat.invite': 'Invite',
  'chat.dialogs': 'Chats',

  // --- Marketplace ----------------------------------------------------------
  'market.title': 'Service requests',
  'market.subtitle': 'Describe the task — masters will respond',
  'market.profile': 'Profile',
  'market.draft': 'Request draft',
  'market.delete_draft': 'Delete draft',
  'market.start_here': 'Where to start',
  'market.recent': 'Recent requests',
  'market.how': 'How it works',
  'market.my_requests': 'My requests',
  'market.new_request': 'New request',
  'market.leave_request': 'Post a request',
  'market.request': 'Request',
  'market.feed': 'Request feed',
  'market.wallet': 'Master wallet',
  'market.wallet_subtitle': 'Balance, holds and history',
  'market.operator': 'Operator cabinet',
  'market.operator_subtitle': 'Masters, refunds and request archive',
  'market.chat': 'Request chat',
  'market.empty': 'No requests yet',
  'market.no_free': 'No available requests',
  'market.not_taken': 'You have not taken any requests yet',
  'market.billing_off': 'Billing is not enabled',
  'market.no_access': 'No access to review',
  'market.login_required': 'Sign-in required',
  'market.done': 'Done',
  'market.photos': 'Photos',
  'market.photos_limit': 'Up to {count} images',
  'market.cancel_request': 'Cancel request?',
  'market.refresh': 'Refresh',
  'market.total': 'Total: {count}',
  'market.masters': 'Masters',
  'market.select_master': 'Find a master',
  'market.hero_title': 'Tell us what needs to be done',
  'market.hero_text': 'A request takes a couple of minutes. Specify the category, address and description — masters will see the task and can respond.',
  'market.continue_draft': 'Continue filling in',
  'market.profile_addresses': 'Profile and my addresses',
  'market.feed_for_master': 'Request feed for masters',
  'market.categories_loading': 'Loading categories…',
  'market.signin_requests': 'Sign in to see your requests and chats with masters.',
  'market.all_requests': 'All requests',
  'market.recent_loading': 'Loading your requests…',
  'market.recent_empty': 'You have no requests yet. Start with the “Post a request” button.',
  'market.draft_saved_device': 'Data is saved on this device',
  'market.draft_saved_at': 'Saved {date}',
  'market.how1': 'Describe the task and specify the address — it takes a couple of minutes.',
  'market.how2': 'Masters will see the request and respond; the selected master appears in the card.',
  'market.how3': 'Chat in the request thread and clarify the details.',
  'market.requests_loading': 'Loading requests…',
  'market.requests_empty_title': 'No requests yet',
  'market.requests_empty_text': 'Create your first request — masters will see it and can respond.',
  'market.free_requests': 'Available requests',
  'market.taken_requests': 'My requests',
  'market.feed_loading_free': 'Loading available requests…',
  'market.feed_loading_mine': 'Loading your requests…',
  'market.feed_subtitle_free': 'For masters · newest first',
  'market.feed_subtitle_mine': 'Requests I have taken',
  'market.wallet_loading': 'Loading wallet…',
  'market.operator_denied_text': 'The operator screen is available to the “Marketplace operator” role and the base administrator.',
  'market.masters_loading': 'Loading masters under review…',
  'market.paid_loading': 'Loading paid requests…',
  'market.archive_loading': 'Loading request archive…',
  'market.review_queue_empty': 'The review queue is empty',
  'market.no_refunds': 'No refunds yet',
  'market.archive_empty': 'The archive is empty',
  'market.service_request': 'Service request',
  'market.request_loading': 'Loading request…',
  'market.step': 'Step {step} of {total} · {title}',
  'market.showcase': 'Services showcase',
  'market.checking_access': 'Checking access',
  'market.mode_error': 'Could not determine the mode',
  'market.back': 'Back',

  // --- Psychology -----------------------------------------------------------
  'psy.title': 'Psychologist',
  'psy.app_unavailable': 'The “Psychologist” application is unavailable',
  'psy.app_unavailable_hint': 'Access is granted to the “Psychologist” or “Psychologist intern” role (assigned by the base administrator). No section is required: all data is in this application.',
  'psy.cabinet_unavailable': 'The patient cabinet is unavailable',
  'psy.cabinet_unavailable_hint': 'This area is intended for the system “Patient” role (a patient account is created when a client card is linked and signs in by invitation). The role is assigned by a specialist/base administrator.',
  'psy.my_cabinet': 'My cabinet',
  'psy.my_data': 'My data',
  'psy.available_tests': 'Available tests',
  'psy.my_results': 'My results',
  'psy.no_results': 'No results yet — take a test.',
  'psy.card_by_specialist': 'The card is filled in by a specialist',
  'psy.loading': 'Loading…',
  'psy.date': 'Date',
  'psy.test': 'Test',
  'psy.score': 'Score',
  'psy.passed': 'Completed',
  'psy.conclusion': 'Conclusion',
  'psy.summary': 'Summary',
  'psy.sessions': 'Sessions',
  'psy.notes': 'Notes',
  'psy.conclusions': 'Conclusions',
  'psy.tests_results': 'Tests and results',
  'psy.reports': 'Reports',
  'psy.patient_cabinet': 'Patient cabinet',
  'psy.patients': 'Patients',
  'psy.new_patient': 'New patient',
  'psy.core_missing': 'HydraMedic core is not installed',
  'psy.core_missing_hint': 'The “Psychologist” application requires the core patient catalog (hydra.medic.core). Install the core package to enable psychologist and patient workplaces.',
  'psy.current_user_missing': 'Current user is not determined — sign in again',
  'psy.cabinet_loading': 'Loading cabinet…',
  'psy.cabinet_mechanism_error': 'Could not determine the cabinet mechanism',
  'psy.retry': 'Retry',
  'psy.take_test': 'Take the test',
  'psy.no_methods': 'No available methods — ask a specialist to assign a test.',
  'psy.protocols_denied': 'Viewing protocols is not allowed for the “Patient” role — results are managed by a specialist (the base administrator grants “Read” for poll types).',
  'psy.card_denied': 'The client card is managed by a specialist — “Read” for the card is not granted to the “Patient” role.',
  'psy.name': 'Full name',
  'psy.phone': 'Phone',
  'psy.email': 'Email',

  'psy.select_patient': 'Select a patient',
  'psy.reports_hint': 'Method reports and printing the “Test conclusion”',
  'psy.gender_male': 'male',
  'psy.gender_female': 'female',
  'psy.patient_default': 'Patient',
  'psy.session_title': 'Session',
  'psy.note_title': 'Note',
  'psy.conclusion_title': 'Conclusion',
  'market.request_canceled': 'Request canceled',
  'market.request_taken': 'Request taken — contacts are open',
  'market.service_area': 'Service area',
  'market.refund_done': 'Refund issued and logged',
  'market.request_not_found': 'Request not found',
  'market.description_missing': 'No description',
  'market.category': 'Category',
  'market.address': 'District and address',
  'market.phone': 'Phone',
  'market.master': 'Master',
  'market.master_not_chosen': 'No master selected yet',
  'market.created': 'Created',
  'market.add': 'Add',
  'market.uploading': 'Uploading {progress}',
  'market.no_photos_hint': 'No photos. Add a picture — it will help the master estimate the task.',
  'market.review_title': 'Review of the master',
  'market.review_hint': 'A review is only possible after a completed request. One client — one review.',
  'market.review_yours': 'Your review',
  'market.review_placeholder': 'Tell how the work went',
  'market.review_submit': 'Leave a review',
  'market.review_sending': 'Sending…',
  'market.review_thanks': 'Thank you! Your review has been published.',
  'market.review_rating': 'Your rating',
  'market.review_list_empty': 'No reviews yet — be the first.',
  'market.review_loading': 'Loading reviews…',

  'admin.users.title': 'Users',
  'admin.users.search': 'Search: name, login, email…',
  'admin.users.create': 'Create user',
  'admin.users.qr': 'Sign-in QR code',
  'admin.users.not_found': 'No users found',
  'admin.users.no_roles': 'no roles',
  'admin.users.system_badge': 'SYS.',
  'admin.users.system_hint': 'Core system account',
  'admin.users.new': 'New user',
  'admin.users.login_required': 'Login (required)',
  'admin.users.name': 'Name',
  'admin.users.password_hint': 'Password (empty = changeme)',
  'admin.users.select': 'Select a user from the list',
  'admin.users.created': 'User created',
  'admin.users.load_error': 'Failed to load users',
  'admin.users.create_error': 'Failed to create',
  'admin.users.devices_load_error': 'Failed to load devices',
  'admin.users.settings_load_error': 'Failed to load the setting',
  'admin.users.sms_allowed': 'SMS gateway is allowed for the user devices',
  'admin.users.sms_denied': 'SMS gateway is disabled for the user devices',
  'admin.users.sms_save_error': 'Could not save the setting',
  'admin.users.request_location': 'Request coordinates',
  'admin.users.request_location_title': 'Location request',
  'admin.users.request_location_body': 'The administrator requested your coordinates. Open the application.',
  'admin.users.location_sent': 'Location request sent to the user',
  'admin.users.location_error': 'Failed to send the request',
  'admin.users.sessions_empty': 'No sign-in history',
  'admin.users.session_failed': 'failed sign-in',
  'admin.users.session_login': 'sign-in',
  'admin.users.created_via.admin': 'Administrator',
  'admin.users.created_via.self_registration': 'Self-registration',
  'admin.users.created_via.auto': 'Auto-created',
  'admin.users.created_via.system': 'System',
  'admin.users.created_via.qr': 'QR code',

  'admin.roles.title': 'Roles and permissions',
  'admin.roles.default_for_new': 'New users receive: {name}',
  'admin.roles.create': 'Create role',
  'admin.roles.none': 'No roles',
  'admin.roles.select': 'Select a role from the list',
  'admin.roles.name_placeholder': 'Role name',
  'admin.roles.desc_placeholder': 'Role description',
  'admin.roles.is_default': 'default',
  'admin.roles.system': 'system',
  'admin.roles.full_access': 'full access',
  'admin.roles.set_default': 'Default',
  'admin.roles.set_default_hint': 'New users will receive this role automatically',
  'admin.roles.default_toast': 'Role “{name}” is the default for new users',
  'admin.roles.save_name': 'Save name and description',
  'admin.roles.tab.perms': 'Permissions',
  'admin.roles.tab.users': 'Users',
  'admin.roles.tab.users_count': 'Users ({count})',
  'admin.roles.tab.permissions': 'Authorities',
  'admin.roles.system_hint': 'System role — permissions are defined by the platform.',
  'admin.roles.perms_hint': 'Role permissions (saved with the button).',
  'admin.roles.custom_placeholder': 'Custom permission, e.g. reports:view',
  'admin.roles.save_perms': 'Save permissions',
  'admin.roles.no_users': 'No users in this role yet',
  'admin.roles.add_user': 'Add user',
  'admin.roles.remove_role': 'Remove role',
  'admin.roles.copy_hint': 'Copy role',
  'admin.roles.delete_hint': 'Delete role',
  'admin.roles.created': 'Role created',
  'admin.roles.saved': 'Role saved',
  'admin.roles.deleted': 'Role deleted',
  'admin.roles.copied': 'Role copied with permissions',
  'admin.roles.perms_saved': 'Permissions saved',
  'admin.roles.user_added': 'User added to the role',
  'admin.roles.load_error': 'Failed to load roles',
  'admin.roles.create_error': 'Failed to create role',
  'admin.roles.update_error': 'Failed to update role',
  'admin.roles.delete_error': 'Failed to delete role',
  'admin.roles.copy_error': 'Failed to copy role',
  'admin.roles.perms_error': 'Failed to save permissions',
  'admin.roles.error': 'Error',
  'admin.roles.confirm_delete': 'Delete role “{name}”?',
  'admin.roles.new_name': 'New role',
  'admin.roles.copy_suffix': ' (copy)',
  'admin.roles.perm.full': 'Full access',
  'admin.roles.perm.full_desc': 'Access to all objects and operations',
  'admin.roles.perm.audit': 'View change history',
  'admin.roles.perm.audit_desc': 'Object change history (audit log)',
  'admin.roles.perm.task_edit': 'Edit created tasks',
  'admin.roles.perm.task_edit_desc': 'Edit/delete any tasks (author and assignee cannot)',

  'app.loading': 'Loading…',
  'app.offline_short': 'Offline mode: data from cache, sync when the network returns',
  'app.offline_long': 'Offline mode: the server is unavailable. Data comes from cache; changes are saved and synced when the connection is restored.',
  'app.connect_error': 'Failed to connect to the base: {error}',
  'app.push_title': 'Enable notifications?',
  'app.push_body': 'Alerts about new messages and tasks, even when the application is closed.',
  'app.tracking_title': 'Location tracking',
  'app.tracking_body': 'Your role policy requires location tracking. Enable it?',
  'app.enable': 'Enable',
  'app.not_now': 'Not now',
  'app.close': 'Close',

  'chat.reply_action': 'Reply',
  'chat.reaction_action': 'React',
  'chat.forward_action': 'Forward',
  'chat.edit_action': 'Edit',
  'chat.delete_action': 'Delete',
  'chat.select_action': 'Select',
  'chat.hide_one_confirm': 'Hide the selected message for me? This cannot be undone.',
  'chat.hide_selected_confirm': 'Hide the selected messages for me ({count})? This cannot be undone.',
  'chat.hide_single_confirm': 'Hide the message for me? This cannot be undone.',
  'chat.voice_load_error': 'Failed to load the voice message',
  'chat.no_files': 'No files to send',
  'chat.empty_file': 'file is empty',
  'chat.choose_participant': 'Choose a participant',
  'chat.open_action': 'Open',
  'chat.pin': 'Pin',
  'chat.unpin': 'Unpin',
  'chat.mute': 'Mute',
  'chat.unmute': 'Unmute',
  'chat.to_archive': 'Archive',
  'chat.from_archive': 'Unarchive',
  'chat.hide_chat': 'Hide chat',
  'chat.show_in_list': 'Show in list',
  'chat.context_discussion': 'Discussion',
  'chat.context_chat': 'Chat',

  'chat.selected_count': 'Selected: {count}',
  'chat.select_messages': 'Select messages',
  'chat.copy_action': 'Copy',
  'chat.cancel_hint': 'Cancel (Esc)',
  'chat.exit_select': 'Exit selection mode',
  'chat.copy_hint': 'Copy the text of the selected messages',
  'chat.forward_hint': 'Forward the selected messages',
  'chat.hide_selected_hint': 'Hide the selected messages for me',
  'chat.selected': 'Selected',
  'chat.not_selected': 'Not selected',
  'chat.forward_title': 'Forward message',
  'chat.forward_many': 'Forward messages ({count})',
  'chat.reply_send': 'Reply',
  'chat.saving': 'Saving…',
  'chat.forwarding_prefix': '➡️ Forward: {label}',

  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'notification.default_title': 'Notification',
  'form.file_info_error': 'Failed to load file details',
  'form.geo_unavailable': 'Coordinates are unavailable — the file was uploaded without them',
  'form.file_upload_error': 'Failed to upload the file',
  'form.file_download_error': 'Failed to download the file',
  'form.file_preview_error': 'Failed to preview the file',
  'form.file_uploaded': 'file uploaded',
  'form.download': 'Download',
  'form.preview': 'Preview',
  'app.session_unavailable': 'The server is unavailable: {target}. No saved session for this base — offline mode is unavailable. Sign in when the server is available.',
  'app.local_server': 'local',
  'app.admin_login_failed': 'Admin/admin authorization failed',

  'chat.clear_self_confirm': 'Do you really want to clear the chat for yourself?',
  'chat.clear_all_confirm': 'Do you really want to delete all messages?',
  'chat.clear_permanent_confirm': 'Delete all messages permanently (without recovery)?',
  'chat.me': 'Me',
  'chat.offline_status': 'offline',
  'form.voice_unavailable': 'Speech recognition is unavailable (Chrome/Edge with a microphone is required).',
  'form.voice_error': 'Recognition error',
  'form.voice_start_error': 'Could not start recognition',
  'form.voice_input': 'Voice input',
  'market.master_login_needed': 'Sign in to take requests.',
  'market.master_offline_take': 'No server connection: taking requests (charging money) is unavailable until the connection is restored.',
  'market.master_billing_off': 'Billing is disabled in the base — paid request taking is unavailable.',
  'market.sync_error': 'Could not apply the request change — re-syncing with the server…',
  'market.master_confirmed': 'Master confirmed',
  'market.master_rejected': 'Master rejected — the reason was saved',
  'market.decision_saved': 'The decision was already saved — the master did not change',
  'market.search_review': 'Name, phone, email or reason',
  'market.search_archive': 'Number, description, address or master',
  'market.review_edit_hint': 'Change the query — search runs by name, contacts and rejection reason.',
  'market.review_empty': 'All masters are reviewed. New requests will appear here automatically.',
  'market.user_missing': 'Current user is not determined — sign in again',

  // --- Common ---------------------------------------------------------------
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.close': 'Close',
  'common.refresh': 'Refresh',
  'common.add': 'Add',
  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.error': 'Error',
  'common.no_data': 'No data',
  'common.create': 'Create',
  'common.edit': 'Editing',
  'common.creating': 'Creating',
  'common.delete': 'Delete',
  'common.saving': 'Saving...',
  'common.saved': 'Saved',
  'common.ok': 'OK',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.more': 'More',
  'common.apply': 'Apply',
  'common.clear': 'Clear',
  'common.reset': 'Reset',
  'common.search': 'Search',
  'common.required': 'required',
  'common.search_placeholder': 'Search…',
  'common.clear_search': 'Clear search',
  'common.voice_search': 'Voice search',
  'common.stop_listening': 'Stop listening',
  'common.remove': 'Remove',
  'common.copy_message': 'Copy message',
  'common.close_notification': 'Close notification',
  'common.done': 'Done',
  'common.canceled': 'Canceled',
  'common.optional': 'optional',

  // --- Errors and dialogs ---------------------------------------------------
  'errors.network_unavailable': 'The server is unavailable. Check your connection and try again.',
  'dialog.title.confirm': 'Confirmation',
  'dialog.title.message': 'Message',
  'dialog.title.input': 'Input',
  'dialog.cancel': 'Cancel',
  'dialog.yes': 'Yes',
  'dialog.ok': 'OK',

  // --- Audit and statuses ---------------------------------------------------
  'audit.created_by': 'Created by',
  'audit.created_at': 'Created at',
  'audit.updated_by': 'Modified by',
  'audit.updated_at': 'Modified at',
  'audit.status_changed': 'Status changed',
  'status.label': 'Status:',
  'status.no_transitions': 'No available transitions',
  'status.choose': 'Choose status',
  'status.choose_title': 'Choose status',
  'status.changing': 'changing status…',
  'status.changed': 'Status changed',

  // --- List period ----------------------------------------------------------
  'period.title': 'Period (by creation date)',
  'period.quick': 'Quick select',
  'period.today': 'Today',
  'period.yesterday': 'Yesterday',
  'period.last7': 'Last 7 days',
  'period.last30': 'Last 30 days',
  'period.this_week': 'This week',
  'period.this_month': 'This month',
  'period.prev_month': 'Previous month',
  'period.this_quarter': 'This quarter',
  'period.this_year': 'This year',
  'period.from': 'From date',
  'period.to_inclusive': 'To date (inclusive)',
  'period.hint': 'The filter applies to all list records by the “Created at” field and is saved for you (list + user setting). “To date” includes the selected day in full.',
  'period.clear': 'Clear fields',
  'period.reset': 'Reset period',
  'period.reset_hint': 'Remove the period filter',

  // --- List settings --------------------------------------------------------
  'list_settings.title': 'List settings',
  'list_settings.tab.group': 'Grouping',
  'list_settings.tab.filters': 'Filters',
  'list_settings.mode.label': 'Display mode',
  'list_settings.mode.none': 'No grouping',
  'list_settings.mode.sections': 'Sections (collapsible)',
  'list_settings.mode.kanban': 'Kanban (columns)',
  'list_settings.mode.chips': 'Summary chips (filter)',
  'list_settings.mode.cards': 'Group cards (drill-down)',
  'list_settings.fields.label': 'Grouping fields (order = nesting: for “group cards” a click opens the next level, then the objects)',
  'list_settings.fields.empty': 'No fields selected — the list has no grouping.',
  'list_settings.fields.add': '+ Add grouping field…',
  'list_settings.example': 'Nesting example: Status → Year → Month → Day: choose “Status”, then “Year · Created at”, “Month · …”, “Day · …” and the “Group cards” mode.',
  'list_settings.filters.hint': 'Filters apply to the list TOGETHER with the form/widget filters (AND) and are saved for you (list + user settings). PERIOD example: for a date field add TWO conditions — “greater than or equal” (from) and “less than or equal” (to); dates in YYYY-MM-DD format. The @current_user token is available as a value.',
  'list_settings.reset': 'Reset to form settings',
  'grouping.year': 'Year',
  'grouping.month': 'Month',
  'grouping.day': 'Day',
  'grouping.move_up': 'Move up',
  'grouping.move_down': 'Move down',

  // --- Form commands --------------------------------------------------------
  'form.no_rights_edit': 'No permission to modify',
  'form.no_rights_create': 'No permission to add',
  'form.no_rights_hint': 'Object rights are configured in the configurator (“Rights” tab)',
  'form.posting': 'Posting...',
  'form.post_and_close': 'Post and close',
  'form.post': 'Post',
  'form.unpost': 'Unpost',
  'form.camera_hint': 'Take a photo and attach it to the object',
  'form.discussion': 'Discussion',
  'form.choose_value': 'Choose a value',
  'form.choose_type_title': 'Choose type',
  'form.choose_type_hint': 'Choose the value type',
  'form.no_types': 'No types',
  'form.choose_value_title': 'Choose a value',
  'form.no_file': 'No file selected',
  'form.choose_file': 'Choose a file',
  'form.camera': 'Take a photo',
  'form.transfer': 'Transfer…',
  'form.restore': 'Restore',
  'form.delete_permanent': 'Delete permanently',
  'form.upload_files': 'Upload files',
  'form.tab.main': 'General',
  'form.tab.tasks': 'Tasks',
  'form.tab.files': 'Files',
  'form.title': '{name} — {action}',
  'form.error.load': 'Failed to load',
  'form.error.save': 'Failed to save',
  'form.error.post': 'Failed to post',
  'form.error.unpost': 'Failed to unpost',
  'form.error.file_load': 'Failed to upload file',
  'form.error.file_download': 'Failed to download file',
  'form.error.delete': 'Failed to delete',
  'form.error.tabular_save': 'Failed to save the tabular section “{name}”',
  'form.error.restore': 'Failed to restore',
  'form.error.status': 'Failed to change status',
  'form.transfer.done': 'Task transferred to the assignee',
  'form.transfer.created': 'Task created and assigned',
  'form.transfer.no_task_type': 'No task type found to assign an assignee',
  'form.comment_title': 'Comment ({action})',
  'form.delete_permanent_confirm': 'Delete permanently? The record will be deleted irrevocably',
  'form.mark_order_delete_confirm': 'Mark the request for deletion? Related tasks will be marked too',
  'form.mark_record_delete_confirm': 'Mark the record for deletion?',
  'form.task_subject_required': 'Save the request first — the task subject needs its id',
  'form.new_group': 'New group',
  'form.type.task': 'Task',
  'form.type.user': 'User',
  'form.type.file': 'File',
  'form.default_order': 'Request',
  'form.default_order_number': 'Request #{number}',

  // --- List commands --------------------------------------------------------
  'list.create': 'Create',
  'list.refresh': 'Refresh',
  'list.create_group': 'Add group',
  'list.cancel': 'Cancel',
  'list.save_filter_folder': 'Save filter as folder',
  'list.show_marked': 'Show marked',
  'list.settings': 'Settings',
  'list.print': 'Print',
  'list.period': 'Period',
  'list.expand': 'Expand',
  'list.collapse': 'Collapse',
  'list.show_more': 'Show more',
  'list.confirm_post': 'Post {count} document(s)?',
  'list.confirm_mark_delete': 'Mark {count} record(s) for deletion?',
  'list.confirm_unpost': 'Unpost {count} document(s)?',
  'list.error.post': 'Failed to post',
  'list.error.unpost': 'Failed to unpost',
  'list.error.group': 'Failed to create group',
  'list.error.folder': 'Failed to save folder',
  'list.folder.user': 'Personal',
  'list.folder.system': 'System',
  'list.folder.shared': 'Shared',
  'list.save_folder_title': 'Save filter as folder',
  'list.save_folder_name': 'Name',
  'list.save_folder_placeholder': 'For example: My tasks for the week',
  'list.save_folder_existing': 'Existing folders',
  'list.save_folder_kind': 'Folder kind',
  'list.save_folder_save': 'Save',
  'list.mark_delete': 'Mark for deletion ({count})',
  'list.delete_permanent': 'Delete permanently ({count})',
  'list.post_count': 'Post ({count})',
  'list.unpost_count': 'Unpost ({count})',
  'list.active_filter': 'Filter active: {count}',
  'list.all': 'All',
  'list.shared_folder': 'Shared folder (visible to all users)',
  'list.save_folder_hint': 'Saves the current filters ({count}) as a folder of this list. @current_user is substituted automatically for each user.',
  'grouping.empty': '(empty)',
  'list.column.owner': 'Owner',
  'list.column.parent': 'Parent',
  'list.column.group': 'Group',
  'list.column.marked': 'Deletion mark',
  'list.column.created_by': 'Created by',
  'list.column.updated_by': 'Modified by',
  'list.column.created_at': 'Created at',
  'list.column.updated_at': 'Modified at',
  'list.column.status': 'Status',
  'list.column.active': 'Active',
  'list.column.files': 'Files',
  'list.column.login': 'Login',
  'list.column.name': 'Name',
  'list.column.email': 'Email',
  'list.column.description': 'Description',

  // --- Messenger (remaining) ------------------------------------------------
  'chat.offline_reply': 'Offline: the reply will be sent when the connection is restored',
  'chat.forwarded_favorites': 'Forwarded to “Favorites”',
  'chat.copied_count': 'Messages copied: {count}',
  'chat.voice_unavailable': 'Voice messages are unavailable in this browser (a microphone and Chrome/Edge are required).',
  'chat.recording': 'Recording a voice message',
  'chat.voice_offline_queued': 'Offline: the voice message will be sent when the connection is restored',
  'chat.transcript_show_full': 'Show full text',
  'chat.transcript_collapse': 'Collapse',
  'chat.edit_transcript_title': 'Edit transcript text',
  'voice.recognizing': 'Recognizing',
  'chat.offline_send': 'Offline: the message will be sent when the connection is restored',
  'chat.offline_send_attachments': 'Offline: the message with attachments will be sent when the connection is restored',
  'chat.offline_geo': 'Offline: the location will be sent when the connection is restored',
  'chat.message_not_found': 'The message was not found in history (it may have been deleted)',
  'chat.dialog_type': 'Type',
  'chat.dialog_direct': 'Direct',
  'chat.dialog_group': 'Group',
  'chat.group_name': 'Group name',
  'chat.group_hint': 'You can create a group WITHOUT members — you will be added automatically (invite members later with the “Invite” button).',
  'chat.members': 'Members',
  'chat.create': 'Create',
  'chat.member_not_in_discussion': 'Member (not in the discussion)',
  'chat.autoplay': 'Play in sequence',
  'chat.autoplay_on': '“Play in sequence”: on — the next voice message starts automatically',
  'chat.autoplay_off': '“Play in sequence”: off',
  'chat.autoplay_on_hint': 'Play in sequence: on (next voice message starts automatically)',
  'chat.autoplay_off_hint': 'Play in sequence: off',

  // --- ECG: MI/HYP findings -------------------------------------------------
  'ecg.mi.badge.screening': 'Screening: suspected myocardial infarction',
  'ecg.mi.badge.confirmed': 'Probable myocardial infarction (confirmed by ST rules)',
  'ecg.mi.text.screening': 'Suspected myocardial infarction (screening). Confirmation is required. Not a diagnosis.',
  'ecg.mi.text.confirmed': 'Probable myocardial infarction by ECG criteria. Not a diagnosis.',
  'ecg.mi_hyp.abstain': 'Conflicting infarction and hypertrophy signs — physician review is required.',
  'ecg.st.prefix': 'ST:',
  'ecg.st.elevation': 'elevation',
  'ecg.st.depression': 'depression',
  'ecg.st.mv': 'mV',
  'ecg.st.none': 'ST: no significant displacement',
  'ecg.st.unavailable': 'ST criteria unavailable',
  'ecg.hyp.title': 'ECG signs of hypertrophy',
  'ecg.hyp.text': 'Echocardiography is required. Not a diagnosis.',
  'ecg.hyp.no_criteria': 'amplitude criteria were not evaluated',
  'ecg.hyp.sokolow': 'Sokolow-Lyon',
  'ecg.hyp.cornell': 'Cornell',
  'ecg.hyp.peguero': 'Peguero-Lo Presti',
  'ecg.hyp.rvh': 'RVH',
  'ecg.hyp.source_label': 'source: {source}',
  'ecg.hyp.source.nn': 'model',
  'ecg.hyp.source.both': 'model and criteria',
  'ecg.hyp.source.rules': 'criteria only',
  'ecg.hyp.source.conflict': 'model and criteria disagree',
  'ecg.conflict': 'Conflicting signs — physician review is required.',

  // --- Phone: number verification -------------------------------------------
  'phone.title': 'Phone',
  'phone.status_verified': 'Number verified',
  'phone.not_verified': 'Number not verified',
  'phone.only_last4': 'only the last 4 digits',
  'phone.edit': 'Change number',
  'phone.saving': 'Saving…',
  'phone.save': 'Save',
  'phone.cancel': 'Cancel',
  'phone.choose_title': 'How to verify the number',
  'phone.no_methods': 'No methods available right now. Contact the base administrator.',
  'phone.sending': 'Sending the request…',
  'phone.wait_title': 'Open Telegram and verify the number',
  'phone.link_until': 'The link is valid until {time}',
  'phone.remaining': '{seconds} s left',
  'phone.open_telegram': 'Open Telegram',
  'phone.open_max': 'Open MAX',
  'phone.done': 'Verified',
  'phone.done_text': 'Number +• (•••) ••• {last4} verified via Telegram.',
  'phone.done_text_max': 'Number +• (•••) ••• {last4} verified via MAX.',
  'phone.done_not_linked': 'The profile mark has not updated yet: the verification center received the confirmation, but the system has not linked it to your account yet. Tell the administrator — no data is lost.',
  'phone.retry': 'Retry',
  'phone.close': 'Close',
  'phone.code_sent_telegram': 'Code sent to Telegram',
  'phone.code_sent_max': 'Code sent to MAX',
  'phone.code_sent_sms': 'Code sent as a message',
  'phone.code_sent': 'Code sent',
  'phone.operator_pending': 'The request is with a specialist — verification will appear here automatically. You can choose another method.',
  'phone.code_enter_below': 'enter it below',
  'phone.expires_in': 'Valid for {seconds} more s',
  'phone.code_placeholder': 'Verification code',
  'phone.checking': 'Checking…',
  'phone.confirm': 'Confirm',
  'phone.confirm_number': 'Verify number',
  'phone.choose_another': 'The request is with a specialist — choose another method',
  'phone.load_failed': 'Could not load the number status',
  'phone.trust_confirmed': 'Number verified at the verification center',
  'phone.expired': 'The verification link has expired — request a new one',
  'phone.rejected': 'Verification was rejected — you can try again',
  'phone.state_failed': 'Could not load the verification status',
  'phone.enter_phone': 'Enter the phone number',
  'phone.saved_hint': 'Number saved. Verify it — changing the number resets verification.',
  'phone.save_failed': 'Could not save the number',
  'phone.set_phone_first': 'Enter and save the phone number first.',
  'phone.base_unknown': 'Could not detect the base — open the profile from the base workspace',
  'phone.telegram_failed': 'Could not send the request to Telegram',
  'phone.sent': 'Request sent',
  'phone.request_failed': 'Could not send the verification request',
  'phone.code_required': 'Enter the verification code',
  'phone.verified_remembered': 'Number verified — this sign-in is remembered',
  'phone.confirmed': 'Number verified',
  'phone.code_invalid': 'Wrong or expired code',
  'phone.new_number_rejected': 'The new number was rejected — access by the verified number is kept',
  'phone.reject_failed': 'Could not reject the new number',
  'phone.loading': 'Checking the phone number…',
  'phone.verified_on': 'Verified on {date}.',
  'phone.new_number_title': 'We see a new number {title}',
  'phone.new_number_text': 'It differs from the verified one in the profile. Verify it with the code from the bot — the profile will not change, the number becomes additional.',
  'phone.not_my_number': 'This is not my number',
  'phone.privacy_note': 'Verification goes through the method you choose — the number is not published.',
  'phone.refresh': 'Refresh',
  'phone.method.telegram': 'Telegram',
  'phone.method.telegram_hint': 'Telegram will open — verify the number with one tap',
  'phone.method.max': 'MAX',
  'phone.method.max_hint': 'MAX will open — verify the number with one tap',
  'trust.confirmed': 'Phone verified',
  'trust.max.unavailable': 'MAX is not running. Open the verification from the MAX app.',
  'trust.max.ready_text': 'Tap the button and share your contact in MAX. The number goes to the verification center signed.',
  'trust.max.init_data_missing': 'MAX did not provide signed data (initData). Open this page from the MAX bot.',
  'phone.method.device': 'On another device',
  'phone.method.telegram_device_hint': 'The code will arrive in your Telegram — enter it here',
  'phone.method.max_device_hint': 'The code will arrive in your MAX — enter it here',
  'phone.method.sms': 'SMS',
  'phone.method.sms_hint': 'The code will arrive as an SMS to the number',
  'phone.method.operator': 'Via a specialist',
  'phone.method.operator_hint': 'A specialist will verify the number — the status updates here',
  'phone.method.other': 'Another method',

  // --- Frontend update (PWA) -------------------------------------------------
  'update.new_version_available': 'A new version is available — reload the app',
  'update.reload': 'Reload',
  'update.auto_applying': 'Applying the update — reloading…',
};

/** Встроенные словари: локаль → сообщения. */
export const MESSAGES: Record<string, Messages> = { ru, en };
